
import { defineComponent } from 'vue';
import ModalLayout from '@/layouts/ModalLayout.vue';
import { popModal } from 'jenesius-vue-modal';
import { ref, watch } from 'vue';
import useRequest from '@/composables/useRequest';
import { SearchUsersRequest } from '@/data/requests/SearchUsersRequest';
import { LottieAnimation } from 'lottie-web-vue';
import { TeamMember } from '@/data/models/TeamMember';
import UserListItem from '@/components/UserListItemElement.vue';
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { SearchUsersRequestResponse } from '@/data/models/SearchUsersRequestResponse';
import { SearchUsersItemRequestResponse } from '@/data/models/SearchUsersItemRequestResponse';
import ButtonElement from '@/components/ButtonElement.vue';
import useConvertPictureURLToResizedURL from '@/composables/useConvertPictureURLToResizedURL';

export default defineComponent({
  props: {
    refreshTeam: {
      type: Function,
      required: true,
    },
  },

  setup() {
    const { t } = useI18n();

    const searchValue = ref('');
    const searchLoading = ref(false);
    const searchResult = ref<TeamMember[] | undefined>();
    const searchError = ref<boolean | undefined>();
    const showLoadMoreButton = ref<boolean>(true);
    const pageSize = 10;
    const loadingLoadMore = ref<boolean>(false);
    const errorLoadMore = ref<boolean>(false);

    watch(
      searchValue,
      _.debounce((newSearch) => {
        searchResult.value = undefined;

        searchLoading.value = true;

        searchError.value = false;

        const { execute, request } = useRequest<SearchUsersRequest, SearchUsersRequestResponse>(
          new SearchUsersRequest(newSearch),
          t('errors.search_users.no_fetch')
        );

        request.value.params.pageSize = pageSize;

        execute()
          .then((response: SearchUsersRequestResponse) => {
            searchResult.value = [];

            response.items.forEach((item: SearchUsersItemRequestResponse) => {
              searchResult.value?.push({
                user_id: item._id.$id,
                name: `${item.name} ${item.lastname}`,
                profile_picture_url: useConvertPictureURLToResizedURL(item._pictures[1].url),
              });
            });

            searchLoading.value = false;
          })
          .catch(() => {
            searchLoading.value = false;
            searchError.value = true;
          });
      }, 1300)
    );

    const inviteUserError = ref<string | undefined>();

    const editErrorInviteUser = (newError: string | undefined) => {
      inviteUserError.value = newError;
    };

    const loadMore = () => {
      loadingLoadMore.value = true;

      const { execute, request } = useRequest<SearchUsersRequest, SearchUsersRequestResponse>(new SearchUsersRequest(searchValue.value), '');

      request.value.params.offset = searchResult.value?.length ?? 0;
      request.value.params.pageSize = pageSize;

      execute()
        .then((response: SearchUsersRequestResponse) => {
          response.items.forEach((item: SearchUsersItemRequestResponse) => {
            searchResult.value?.push({
              user_id: item._id.$id,
              name: `${item.name} ${item.lastname}`,
              profile_picture_url: useConvertPictureURLToResizedURL(item._pictures[1].url),
            });
          });

          if (response.items.length === pageSize) {
            showLoadMoreButton.value = true;
          } else {
            showLoadMoreButton.value = false;
          }

          loadingLoadMore.value = false;
        })
        .catch(() => {
          errorLoadMore.value = true;
          loadingLoadMore.value = false;
        });
    };

    return {
      popModal,
      searchValue,
      searchLoading,
      searchResult,
      inviteUserError,
      editErrorInviteUser,
      searchError,
      showLoadMoreButton,
      loadMore,
      errorLoadMore,
      loadingLoadMore,
    };
  },
  components: {
    UserListItem,
    LottieAnimation,
    ModalLayout,
    ButtonElement,
  },
});
